/**
 * theme.ts
 *
 * This file is responsible for creating the Material-UI theme for the application.
 * It imports the design tokens defined in designTokens.ts and uses them to define
 * the color palette and component styles for the MUI theme.
 *
 * The theme object is then provided to the entire application using the ThemeProvider
 * component from MUI, ensuring consistent styling across all components.
 */

import './themeAugmentation';

import { createTheme, TypographyProps } from '@mui/material';

import { designTokens } from './designTokens';

export type CustomTypographyVariant = TypographyProps['variant'] | 'caption1' | 'caption2';

const theme = createTheme({
  palette: {
    base: designTokens.base,
    utility: designTokens.utility,
  },
  typography: {
    fontFamily: 'Arial',
    h1: {
      color: '#070154',
      fontSize: '56px',
      lineHeight: '62px',
      letterSpacing: '0.01px',
      fontWeight: 800,
    },
    h2: {
      color: '#070154',
      fontSize: '42px',
      lineHeight: '52px',
      letterSpacing: '0.01px',
      fontWeight: 500,
    },
    h3: {
      color: '#070154',
      fontSize: '36px',
      lineHeight: '46px',
      letterSpacing: '0.01px',
      fontWeight: 500,
    },
    h4: {
      color: '#070154',
      fontSize: '36px',
      lineHeight: '46px',
      letterSpacing: '0.01px',
      fontWeight: 500,
    },
    h5: {
      color: '#070154',
      fontSize: '22px',
      lineHeight: '28px',
      letterSpacing: '0.01px',
      fontWeight: 800,
    },
    h6: {
      color: '#070154',
      fontSize: '22px',
      lineHeight: '28px',
      letterSpacing: '0.01px',
      fontWeight: 800,
    },
    subtitle1: {
      color: '#070154',
      fontSize: '18px',
      lineHeight: '24px',
      letterSpacing: '0.01px',
      fontWeight: 700,
    },
    subtitle2: {
      color: '#070154',
      fontSize: '16px',
      lineHeight: '20px',
      letterSpacing: '0.01px',
      fontWeight: 700,
    },
    body1: {
      color: '#070154',
      fontSize: '16px',
      // Commenting this out for now to maintain the current
      // look of Nigel. The MUI body1 lineHeight default
      // seems to be 3, so 1.5 would bring lines closer together
      // lineHeight: '1.5rem',
      letterSpacing: '0.01px',
      fontWeight: 400,
    },
    body2: {
      color: '#070154',
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '0.01px',
      fontWeight: 400,
    },
    button: {
      fontSize: '14px',
      // Commenting this out for now to maintain the current
      // look of Nigel. The MUI Button lineHeight default
      // seems to be 1.6, so 0.875 would bring lines closer together
      // lineHeight: '0.875rem',
      letterSpacing: '0.01px',
      fontWeight: 500,
      textTransform: 'none',
    },
    caption1: {
      fontSize: '12px',
      lineHeight: '12px',
      letterSpacing: '-0.2px',
      fontWeight: 400,
    },
    caption2: {
      fontSize: '11px',
      lineHeight: '11px',
      letterSpacing: '-0.2px',
      fontWeight: 400,
    },
  },
  components: {
    MuiAlert: {
      styleOverrides: {
        root: {
          fontFamily: 'IBM Plex Sans',
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '0px',
        },
        contained: {
          backgroundColor: '#070154',
          '&:hover': {
            backgroundColor: '#0047FF',
          },
          '&:active': {
            backgroundColor: '#CED7E6',
          },
        },
        outlined: {
          color: '#092b49',
          borderColor: '#092b49',

          ':hover': {
            color: '#135996',
            borderColor: '#135996',
          },

          ':active': {
            color: '#1C86E3',
            borderColor: '#1C86E3',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          marginRight: 'auto',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: 'Arial',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#0047FF',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          fontFamily: 'Arial',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: 'Arial',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          borderColor: '#CED7E6',
          '&:hover:not(.Mui-disabled)::before': {
            borderColor: '#F900D3',
          },
          '&.Mui-focused::after': {
            borderColor: '#50658E',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderColor: '#CED7E6',
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: '#F900D3',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#50658E',
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#E8EEF8',
          border: '1px solid #50658E',
          fontFamily: 'Arial',
          color: '#070154',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: 'Arial',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Arial',
          color: '#070154',

          '& > *': {
            fontFamily: 'Arial',
          },
        },
      },
    },
  },
});

export default theme;
